import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { injectIntl, FormattedMessage } from 'sb-react-intl'
import { compose } from 'common/helpers'

import Href from 'components/ui/Href/Href'
import Message from 'components/text/Message/Message'

import messages from './messages'
import s from './Button.scss'

import arrowImage from './images/right-arrow.svg'


const Button = (incomeProps) => {
  const {
    intl,

    // basic
    className, onClick,

    // sizes
    xsm, sm, md, lg, xlg,
    noSize, // noSize is ductape prop to prevent setting default "lg" className if no size passed

    // modifiers
    uppercase, fullWidth, fullWidthOnMobile, noPadding, halfPadding, doublePadding, disabled, arrowed, loading,

    // colors
    brand,
    brandWhite, // TODO rewrite
    white, whiteWithBorder, whiteBrand, linkAlike, whiteGray, // TODO rewrite
    gray, lightGray, emptyGray, // TODO rewrite
    textButton, blackTextButton, linkedin, danger, transparent, // TODO rewrite

    // links
    to, toOLD, redirect,

    // content
    children, title,

    // ADA
    ariaLive,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,

    // misc
    tag, type, id, dataAttributes, dataTestId,

    'data-s-onclick-track-mixpanel': trackMixpanel,
    'data-s-onclick-track-custom-fbq': trackFbq,
    'data-s-mixpanel-placement': mixpanelPlacement,
    'data-s-onclick-track-mixpanel-properties': mixpanelProperties,
    'data-s-onclick-track-mixpanel-with-snowplow': withSnowplow,
    'data-position': mixpanelPosition,

  } = incomeProps

  if (!xsm && !sm && !md && !lg && !xlg) {
    console.warn('There is Button component without "size" property!')
  }

  const isLg = lg || (!noSize && !xsm && !sm && !md && !xlg)

  const buttonClassName = cx(s.button, className, 'button', {

    // sizes
    [s.xsm]: xsm,   // 26
    [s.sm]: sm,     // 32
    [s.md]: md,     // 38
    [s.lg]: isLg,   // 44
    [s.xlg]: xlg,   // 50

    // modifiers
    [s.fullWidth]: fullWidth,
    [s.fullWidthOnMobile]: fullWidthOnMobile,
    [s.noPadding]: noPadding,
    [s.halfPadding]: halfPadding,
    [s.doublePadding]: doublePadding,
    [s.uppercase]: uppercase,
    [s.disabled]: disabled,
    [s.loading]: loading,

    // colors
    [s.brand]: brand,
    [s.gray]: gray,
    [s.lightGray]: lightGray,
    [s.emptyGray]: emptyGray,
    [s.white]: white,
    [s.whiteWithBorder]: whiteWithBorder,
    [s.whiteBrand]: whiteBrand,
    [s.linkAlike]: linkAlike,
    [s.brandWhite]: brandWhite,
    [s.whiteGray]: whiteGray,
    [s.textButton]: textButton,
    [s.blackTextButton]: blackTextButton,
    [s.linkedin]: linkedin,
    [s.transparent]: transparent,
  })

  // Node ---------------------------------------------- /

  let node = tag

  if (to || toOLD || redirect) {
    node = Href
  }

  // Props -------------------------------------------- /

  let props = {
    className: buttonClassName,
    id,
    type,
    disabled,
    onClick: (event) => {
      if (!loading && typeof onClick === 'function') {
        onClick(event)
      }
    },
    'data-testid': dataTestId,
    'data-s-onclick-track-mixpanel': trackMixpanel,
    'data-s-onclick-track-custom-fbq': trackFbq,
    'data-s-mixpanel-placement': mixpanelPlacement,
    'data-s-onclick-track-mixpanel-properties': mixpanelProperties,
    'data-s-onclick-track-mixpanel-with-snowplow': withSnowplow,
    'data-position': mixpanelPosition,
    'aria-live': ariaLive,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  }

  if (to || toOLD || redirect) {
    props = {
      ...props,
      to,
      toOLD,
      redirect,
    }

    delete props.type
  }

  if (dataAttributes) {
    props = {
      ...props,
      ...dataAttributes,
    }
  }

  // Content ------------------------------------------ /

  let content
  let arrow
  let loader

  if (loading) {
    content = <FormattedMessage className={s.title} {...messages.loading} />

    if (!ariaLabel && !ariaLabelledBy) {
      props['aria-label'] = intl.formatMessage(messages.loading)
    }
  }
  else if (title) {
    content = <Message className={s.title} text={title} html />

    if (!ariaLabel && !ariaLabelledBy) {
      const isIntl          = typeof title === 'object'
      const titleAriaLabel  = isIntl ? intl.formatHTMLMessage(title) : title
      const isHtmlAriaLabel = isIntl && /<.+>/.test(titleAriaLabel)

      if (!isHtmlAriaLabel) {
        props['aria-label'] = titleAriaLabel
      }
    }
  }
  else {
    content = <span className={s.title}>{children}</span>
  }

  if (arrowed) {
    arrow = <img className={s.arrow} src={arrowImage} alt="" />
  }

  if (loading) {
    loader = <div className={s.loader} />
  }

  // eslint-disable-next-line react/destructuring-assignment
  if (!props['aria-label'] && !props['aria-labelledby']) {
    console.warn('There is Button component without ariaLabel or ariaLabelledBy property')
  }

  return React.createElement(
    node,
    props,
    content,
    arrow,
    loader,
  )
}

Button.propTypes = {
  // basic
  className: PropTypes.string,
  onClick: PropTypes.func,

  // sizes
  xsm: PropTypes.bool,
  sm: PropTypes.bool,
  md: PropTypes.bool,
  lg: PropTypes.bool,
  xlg: PropTypes.bool,

  // modifiers
  uppercase: PropTypes.bool,
  fullWidth: PropTypes.bool,
  fullWidthOnMobile: PropTypes.bool,
  noPadding: PropTypes.bool,
  halfPadding: PropTypes.bool,
  doublePadding: PropTypes.bool,
  disabled: PropTypes.bool,
  arrowed: PropTypes.bool,
  loading: PropTypes.bool,

  // colors
  brand: PropTypes.bool,
  gray: PropTypes.bool,
  lightGray: PropTypes.bool,
  emptyGray: PropTypes.bool,
  white: PropTypes.bool,
  whiteWithBorder: PropTypes.bool,
  whiteBrand: PropTypes.bool,
  linkAlike: PropTypes.bool,
  brandWhite: PropTypes.bool,
  whiteGray: PropTypes.bool,
  textButton: PropTypes.bool,
  linkedin: PropTypes.bool,
  transparent: PropTypes.bool,

  // links
  to: PropTypes.string,
  toOLD: PropTypes.string,
  redirect: PropTypes.string,

  // content
  children: PropTypes.any,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),

  // misc
  tag: PropTypes.string,
  type: PropTypes.string,
  id: PropTypes.string,
  dataTestId: PropTypes.string,

  // ADA
  ariaLive: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
}

Button.defaultProps = {
  tag: 'button',
  uppercase: true,
}


export default compose(
  injectIntl,
  React.memo,
)(Button)
